import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import { PaddleWebhookEvent } from '../../../../types/admin';
import { format } from 'date-fns';

interface WebhookEventDetailModalProps {
  open: boolean;
  event: PaddleWebhookEvent;
  onClose: () => void;
}

export default function WebhookEventDetailModal({ open, event, onClose }: WebhookEventDetailModalProps) {
  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item xs={4}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <Typography variant="body2">{value}</Typography>
      </Grid>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Webhook Event Details
        <Typography variant="body2" color="text.secondary">
          {event.id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {event.processing_status === 'failed' && (
          <Alert severity="error" sx={{ mb: 3 }}>
            This webhook event failed to process. Error: {event.error_message}
          </Alert>
        )}

        {event.processing_status === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            This webhook event was processed successfully.
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Event Information
          </Typography>
          <DetailRow
            label="Event Type"
            value={<Chip label={event.event_type} size="small" color="primary" variant="outlined" />}
          />
          <DetailRow
            label="Processing Status"
            value={
              <Chip
                label={event.processing_status.toUpperCase()}
                size="small"
                color={event.processing_status === 'success' ? 'success' : 'error'}
              />
            }
          />
          <DetailRow label="Paddle Subscription ID" value={event.paddle_subscription_id} />
          <DetailRow label="Retry Count" value={event.retry_count} />
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Timing
          </Typography>
          <DetailRow label="Received At" value={format(new Date(event.received_at), 'PPP p')} />
        </Box>

        {event.error_message && (
          <>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Error Details
              </Typography>
              <DetailRow label="Error Message" value={event.error_message} />
            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Payload
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
              {JSON.stringify(event.payload, null, 2)}
            </pre>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
